import React, { useState, useEffect } from 'react';
import { 
  getUserBackupHistory, 
  restoreUserFromBackup, 
  getLatestUserBackup,
  getUserBackupStats,
  createInitialBackupsForAllUsers,
  createCurrentStateBackupsForAllUsers,
  cleanupAllUserBackups
} from '../utils/userBackupSystem';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-toastify';
import './UserBackupManager.css';

const UserBackupManager = () => {
  const [userId, setUserId] = useState('');
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [showBackupDetails, setShowBackupDetails] = useState(false);

  const loadUserBackups = async () => {
    if (!userId) {
      toast.error('Please enter a User ID');
      return;
    }

    setLoading(true);
    try {
      const [backupsResult, statsResult] = await Promise.all([
        getUserBackupHistory(userId, 100),
        getUserBackupStats(userId)
      ]);

      if (backupsResult.success) {
        console.log('Backups loaded:', backupsResult.backups);
        setBackups(backupsResult.backups);
      } else {
        toast.error('Failed to load backups: ' + backupsResult.error);
      }

      if (statsResult.success) {
        setStats(statsResult.stats);
      }
    } catch (error) {
      console.error('Error loading backups:', error);
      toast.error('Failed to load backup data');
    } finally {
      setLoading(false);
    }
  };

  const restoreFromBackup = async (backupId) => {
    if (!userId || !backupId) {
      toast.error('Missing user ID or backup ID');
      return;
    }

    if (!window.confirm('⚠️ Are you sure you want to restore from this backup? This will overwrite current user data!')) {
      return;
    }

    setLoading(true);
    try {
      const result = await restoreUserFromBackup(userId, backupId);
      
      if (result.success) {
        // Update the user document with restored data
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, result.backupData);
        
        toast.success('✅ User data restored successfully!');
        console.log('User restored from backup:', result);
        
        // Reload backups to show the new pre-restore backup
        await loadUserBackups();
      } else {
        toast.error('Failed to restore: ' + result.error);
      }
    } catch (error) {
      console.error('Error restoring user:', error);
      toast.error('Failed to restore user data');
    } finally {
      setLoading(false);
    }
  };

  const createInitialBackups = async () => {
    if (!window.confirm('⚠️ This will create initial backups for ALL users. This may take a while. Continue?')) {
      return;
    }

    setLoading(true);
    try {
      const result = await createInitialBackupsForAllUsers();
      
      if (result.success) {
        toast.success(`✅ Initial backups created: ${result.successful} successful, ${result.failed} failed`);
        console.log('Initial backup result:', result);
      } else {
        toast.error('Failed to create initial backups: ' + result.error);
      }
    } catch (error) {
      console.error('Error creating initial backups:', error);
      toast.error('Failed to create initial backups');
    } finally {
      setLoading(false);
    }
  };

  const createCurrentStateBackups = async () => {
    if (!window.confirm('⚠️ Are you sure you want to create current state backups for all users? This will update the current state backup for each user.')) {
      return;
    }

    setLoading(true);
    try {
      const result = await createCurrentStateBackupsForAllUsers();
      
      if (result.success) {
        toast.success(`✅ Current state backups created: ${result.successful} successful, ${result.failed} failed`);
        console.log('Current state backup result:', result);
      } else {
        toast.error('❌ Current state backup creation failed: ' + result.error);
      }
    } catch (error) {
      console.error('Error creating current state backups:', error);
      toast.error('Failed to create current state backups');
    } finally {
      setLoading(false);
    }
  };

  const cleanupAllBackups = async () => {
    if (!window.confirm('⚠️ This will clean up ALL user backups to keep only the 3 most recent per user. This will delete old backups. Continue?')) {
      return;
    }

    setLoading(true);
    try {
      const result = await cleanupAllUserBackups();
      
      if (result.success) {
        toast.success(`✅ Cleanup completed: ${result.totalCleaned} backups deleted across ${result.usersProcessed} users`);
        console.log('Cleanup result:', result);
      } else {
        toast.error('Failed to cleanup backups: ' + result.error);
      }
    } catch (error) {
      console.error('Error cleaning up backups:', error);
      toast.error('Failed to cleanup backups');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getChangeTypeColor = (changeType, isCurrentState = false) => {
    if (isCurrentState) {
      return '#10b981'; // Green for current state
    }
    
    const colors = {
      'dp_purchase': '#4ade80',
      'match_result': '#3b82f6',
      'profile_update': '#8b5cf6',
      'initial_backup': '#f59e0b',
      'pre_restore_backup': '#ef4444',
      'admin_action': '#6366f1',
      'current_state': '#10b981'
    };
    return colors[changeType] || '#6b7280';
  };

  return (
    <div className="user-backup-manager">
      <div className="backup-header">
        <h2>🔄 User Data Backup Manager</h2>
        <p>Manage and restore user data backups</p>
      </div>

      <div className="backup-controls">
        <div className="control-group">
          <label>User ID (UID)</label>
          <div className="input-group">
            <input
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="Enter user ID"
            />
            <button onClick={loadUserBackups} disabled={loading || !userId}>
              {loading ? 'Loading...' : 'Load Backups'}
            </button>
          </div>
        </div>

        <div className="control-group">
          <button 
            className="create-initial-backups"
            onClick={createInitialBackups}
            disabled={loading}
          >
            🚀 Create Initial Backups for All Users
          </button>
          <button 
            className="create-current-state-backups"
            onClick={createCurrentStateBackups}
            disabled={loading}
          >
            💾 Create Current State Backups for All Users
          </button>
          <button 
            className="cleanup-backups"
            onClick={cleanupAllBackups}
            disabled={loading}
          >
            🧹 Cleanup Old Backups (Keep 3 Most Recent)
          </button>
        </div>
      </div>

      {stats && (
        <div className="backup-stats">
          <h3>Backup Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Total Backups:</span>
              <span className="stat-value">{String(stats.totalBackups || 0)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Latest Backup:</span>
              <span className="stat-value">{formatTimestamp(stats.latestBackup)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Data Size:</span>
              <span className="stat-value">{String(((stats.dataSize || 0) / 1024).toFixed(2))} KB</span>
            </div>
          </div>
        </div>
      )}

      {backups.length > 0 && (
        <div className="backups-list">
          <h3>Backup History</h3>
          <div className="backups-grid">
            {backups.map((backup) => (
              <div key={backup.id} className="backup-card">
                <div className="backup-header">
                  <div className="backup-version">
                    {backup.isCurrentState ? 'CURRENT' : `v${String(backup.version || '1')}`}
                  </div>
                  <div 
                    className="backup-type"
                    style={{ backgroundColor: getChangeTypeColor(backup.changeType, backup.isCurrentState) }}
                  >
                    {backup.isCurrentState ? 'CURRENT STATE' : String(backup.changeType || 'Unknown')}
                  </div>
                </div>
                
                <div className="backup-info">
                  <div className="backup-time">
                    {formatTimestamp(backup.timestamp)}
                  </div>
                  <div className="backup-trigger">
                    Triggered by: {String(backup.triggeredBy || 'Unknown')}
                  </div>
                  {backup.changeDetails?.changes && (
                    <div className="backup-changes">
                      Changes: {backup.changeDetails.changes.length} fields
                    </div>
                  )}
                </div>

                <div className="backup-actions">
                  <button
                    className="view-details-btn"
                    onClick={() => {
                      setSelectedBackup(backup);
                      setShowBackupDetails(true);
                    }}
                  >
                    View Details
                  </button>
                  <button
                    className="restore-btn"
                    onClick={() => restoreFromBackup(backup.id)}
                    disabled={loading}
                  >
                    Restore
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {backups.length === 0 && !loading && userId && (
        <div className="no-backups">
          <p>No backups found for this user</p>
        </div>
      )}

      {/* Backup Details Modal */}
      {showBackupDetails && selectedBackup && (
        <div className="backup-details-modal" onClick={() => setShowBackupDetails(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Backup Details - Version {selectedBackup.version}</h3>
              <button 
                className="close-btn"
                onClick={() => setShowBackupDetails(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="backup-metadata">
                <p><strong>Change Type:</strong> {String(selectedBackup.changeType || 'Unknown')}</p>
                <p><strong>Triggered By:</strong> {String(selectedBackup.triggeredBy || 'Unknown')}</p>
                <p><strong>Timestamp:</strong> {formatTimestamp(selectedBackup.timestamp)}</p>
                <p><strong>Data Hash:</strong> {String(selectedBackup.dataHash || 'N/A')}</p>
              </div>
              
              {selectedBackup.changeDetails?.changes && (
                <div className="changes-list">
                  <h4>Changes Made:</h4>
                  {selectedBackup.changeDetails.changes.map((change, index) => (
                    <div key={index} className="change-item">
                      <strong>{change.field}:</strong>
                      <span className="old-value">{JSON.stringify(change.oldValue)}</span>
                      <span className="arrow">→</span>
                      <span className="new-value">{JSON.stringify(change.newValue)}</span>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="backup-data-preview">
                <h4>Backup Data Preview:</h4>
                <div className="backup-data-summary">
                  {selectedBackup.backupData && (
                    <>
                      <div className="data-field">
                        <strong>Pseudo:</strong> {String(selectedBackup.backupData.pseudo || 'N/A')}
                      </div>
                      <div className="data-field">
                        <strong>DP:</strong> {String(selectedBackup.backupData.dp || 0)}
                      </div>
                      <div className="data-field">
                        <strong>Master Duel ID:</strong> {String(selectedBackup.backupData.masterDuelId || 'N/A')}
                      </div>
                      <div className="data-field">
                        <strong>EDOPro Pseudo:</strong> {String(selectedBackup.backupData.edoproPseudo || 'N/A')}
                      </div>
                      <div className="data-field">
                        <strong>Facebook Link:</strong> {String(selectedBackup.backupData.facebookLink || 'N/A')}
                      </div>
                      <div className="data-field">
                        <strong>Profile Picture:</strong> 
                        {selectedBackup.backupData.profilePicture ? (
                          <img 
                            src={String(selectedBackup.backupData.profilePicture)} 
                            alt="Profile" 
                            style={{width: '50px', height: '50px', objectFit: 'cover', borderRadius: '50%', marginLeft: '10px'}}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'inline';
                            }}
                          />
                        ) : (
                          <span>N/A</span>
                        )}
                        <span style={{display: 'none'}}>Image failed to load</span>
                      </div>
                      <div className="data-field">
                        <strong>Banner Picture:</strong> 
                        {selectedBackup.backupData.bannerPicture ? (
                          <img 
                            src={String(selectedBackup.backupData.bannerPicture)} 
                            alt="Banner" 
                            style={{width: '100px', height: '30px', objectFit: 'cover', borderRadius: '4px', marginLeft: '10px'}}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'inline';
                            }}
                          />
                        ) : (
                          <span>N/A</span>
                        )}
                        <span style={{display: 'none'}}>Image failed to load</span>
                      </div>
                      <div className="data-field">
                        <strong>Division:</strong> {String(selectedBackup.backupData.division || 'N/A')}
                      </div>
                      <div className="data-field">
                        <strong>Stats:</strong> {JSON.stringify(selectedBackup.backupData.stats || {})}
                      </div>
                    </>
                  )}
                </div>
                <details>
                  <summary>Raw JSON Data</summary>
                  <pre>{JSON.stringify(selectedBackup.backupData || {}, null, 2)}</pre>
                </details>
              </div>
              
              {selectedBackup.userData && (
                <div className="user-data-preview">
                  <h4>User Data:</h4>
                  <pre>{JSON.stringify(selectedBackup.userData, null, 2)}</pre>
                </div>
              )}
              
              {selectedBackup.changeDetails && (
                <div className="change-details-preview">
                  <h4>Change Details:</h4>
                  <pre>{JSON.stringify(selectedBackup.changeDetails, null, 2)}</pre>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                className="restore-btn"
                onClick={() => {
                  restoreFromBackup(selectedBackup.id);
                  setShowBackupDetails(false);
                }}
                disabled={loading}
              >
                Restore This Backup
              </button>
              <button 
                className="cancel-btn"
                onClick={() => setShowBackupDetails(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserBackupManager;
